import React, { useState } from 'react';
import { Layout, Menu, Space, Avatar, Dropdown, Button, Drawer, Grid } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  DashboardOutlined,
  BankOutlined,
  TeamOutlined,
  BarChartOutlined,
  FileTextOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  MenuOutlined,
  AppstoreOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import logo from '../../assets/logo.svg';
import { useTranslation } from 'react-i18next';

const { Header } = Layout;
const { useBreakpoint } = Grid;

const AppHeader = () => {
  const [drawerVisible, setDrawerVisible] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  // Helper function to check if user has a specific group
  const hasGroup = (group) => user?.groups?.includes(group);

  // Get menu items based on user groups
  const getMenuItems = () => {
    const items = [
      {
        key: '/dashboard',
        icon: <DashboardOutlined />,
        label: t('navigation.main.dashboard'),
      },
      {
        key: '/billing',
        icon: <BankOutlined />,
        label: t('navigation.main.billing'),
      },     
      {
        key: '/loans',
        icon: <BankOutlined />,
        label: t('navigation.main.loans'),
      },
      {
        key: '/customers',
        icon: <TeamOutlined />,
        label: t('navigation.main.clients'),
      }
      // ,
      // {
      //   key: '/my-analytics',
      //   icon: <BarChartOutlined />,
      //   label: t('navigation.main.myAnalytics'),
      // },
    ];

    // Admin menu items
    const adminItems = [];

    // Only show admin items if user is consultant-admin
    if (hasGroup('Consultant-Admin')) {
      adminItems.push(
        {
          key: '/analytics',
          label: t('navigation.main.consultantAnalytics'),
        },
        {
          key: '/bank-analytics',
          label: t('navigation.main.bankAnalytics'),
        },     
        {
          key: '/admin/banks',
          label: t('navigation.main.banks')
        },
        {
          key: '/admin/referrers',
          label: t('navigation.main.referrers')
        }  
      );
    }

    // Only show accountant items if user is accountant
    if (hasGroup('Accountant')) {
      adminItems.push(
          {
          key: 'accountant/withdrawal-passed',
          label: t('navigation.main.withdrawalPassed')
          },
          {
          key: 'accountant/payment-confirmed',
          label: t('navigation.main.paymentConfirmed')
          },
          {
            key: '/accountant/consultant-billing-admin-overview',
            label: t('navigation.accountant.consultantBillingAdminOverview'),
          },
          {
            key: '/accountant/consultant-billing-admin',
            label: t('navigation.accountant.consultantBillingAdmin'),
          },
          {
            key: '/admin/loans',
            label: t('navigation.accountant.loansAdmin'),
          }          
      );
    }

    // Only add administration menu if there are admin items
    if (adminItems.length > 0) {
      items.push({
        icon: <SettingOutlined />,
        label: t('navigation.main.administration'),
        children: adminItems
      });
    }

    return items;
  };

  const menuItems = getMenuItems();

  // Add settings and logout to mobile menu
  const mobileMenuItems = [
    ...menuItems,
    {
      type: 'divider',
      style: { borderColor: 'rgba(255, 255, 255, 0.1)' }
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: t('navigation.main.settings')
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('navigation.main.logout')
    }
  ];

  const userMenu = {
    items: [
      {
        key: 'settings',
        icon: <SettingOutlined />,
        label: t('navigation.main.settings')
      },
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: t('navigation.main.logout')
      },
    ],
    onClick: ({ key }) => {
      if (key === 'logout') {
        navigate('/logout');
      } else if (key === 'settings') {
        navigate('/settings');
      }
    },
  };

  const handleMenuClick = ({ key }) => {
    if (key === 'logout') {
      navigate('/logout');
    } else if (key === 'settings') {
      navigate('/settings');
    } else {
      navigate(key);
    }
    if (isMobile) {
      setDrawerVisible(false);
    }
  };

  return (
    <Header style={{
      position: 'fixed',
      width: '100%',
      height: isMobile ? 64 : 'auto',
      padding: 0,
      background: '#2C4B39',
      color: '#fff',
      borderBottom: '1px solid #f0f0f0',
      zIndex: 100,
    }}>
      <div style={{
        margin: '0 auto',
        padding: '0 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '100%',
      }}>
        {isMobile ? (
          <>
            <div style={{ 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
            }}>
              <img
                src={logo}
                alt="Logo"
                style={{ 
                  height: 16,
                  maxWidth: 120,
                  maxHeight: '80%',
                }}
              />
            </div>
            <Button
              type="text"
              icon={<MenuOutlined style={{ color: '#fff' }} />}
              onClick={() => setDrawerVisible(true)}
              style={{ padding: '8px 0' }}
            />
          </>
        ) : (
          <>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center',
              gap: 16,
              flex: 1,
              overflow: 'hidden',
            }}>
              <img
                src={logo}
                alt="Logo"
                style={{ 
                  height: 16,
                  maxWidth: 160,
                  maxHeight: '80%',
                  flexShrink: 0,
                }}
              />
              <Menu
                theme="dark"
                mode="horizontal"
                selectedKeys={[location.pathname]}
                items={menuItems}
                onClick={handleMenuClick}
                style={{
                  flex: 1,
                  minWidth: 0,
                  background: 'transparent',
                  width: '100%',
                }}
                overflowedIndicator={<MenuOutlined />}
              />
            </div>
            <Dropdown
              menu={userMenu}
              placement="bottomRight"
              trigger={['click']}
            >
              <Space style={{ cursor: 'pointer', color: '#fff' }}>
                <span style={{ 
                  maxWidth: 200,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {user?.first_name + ' ' + user?.last_name || t('common.user')}
                </span>
              </Space>
            </Dropdown>
          </>
        )}
      </div>

      <Drawer
        title={user?.first_name + ' ' + user?.last_name || t('common.user')}
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        width={280}
        styles={{
          header: {
            background: '#2C4B39',
            color: '#fff',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          },
          body: {
            padding: 0,
            background: '#2C4B39',
          },
          content: {
            background: '#2C4B39',
          },
          wrapper: {
            background: '#2C4B39',
          },
        }}
        closeIcon={<MenuOutlined style={{ color: '#fff' }} />}
      >
        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          items={mobileMenuItems}
          onClick={handleMenuClick}
          style={{ 
            borderRight: 'none',
            background: '#2C4B39',
            color: '#fff',
          }}
          theme="dark"
        />
      </Drawer>
    </Header>
  );
};

export default AppHeader;
